import { useEffect, useState, useCallback } from "react";
import { API } from "@/App";
import { toast } from "sonner";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { MessageCircle, Copy, Check, Link2 } from "lucide-react";
import { DateInputTR } from "@/components/ui/date-picker-tr";

const markSent = (id, sent) =>
  fetch(`${API}/reservations/${id}/reminder-sent`, {
    method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ sent }),
  });

const AttendeeRow = ({ a, onToggle }) => (
  <div className="flex flex-wrap items-center gap-2 py-1.5 border-b last:border-0 text-sm" data-testid={`reminder-row-${a.reservation_id}`}>
    <span className="font-medium min-w-[140px]">{a.member_name}</span>
    {a.via_link && <Link2 className="w-3 h-3 text-emerald-600" title="Linkten" />}
    <span className="text-xs text-grey">{a.phone || "Telefon yok"}</span>
    <div className="flex-1" />
    {a.wa_link ? (
      <a href={a.wa_link} target="_blank" rel="noreferrer" onClick={() => !a.reminder_sent && onToggle(a, true)}
        className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 text-white text-xs px-2.5 py-1 hover:bg-emerald-700" data-testid={`reminder-wa-${a.reservation_id}`}>
        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
      </a>
    ) : <span className="text-xs text-red-600">Numara yok</span>}
    <button onClick={() => onToggle(a, !a.reminder_sent)} title="Gönderildi olarak işaretle"
      className={`w-7 h-7 rounded-lg border flex items-center justify-center transition-colors ${a.reminder_sent ? "bg-emerald-100 border-emerald-300 text-emerald-700" : "border-slate-200 text-slate-300 hover:text-slate-500"}`}
      data-testid={`reminder-sent-toggle-${a.reservation_id}`}>
      <Check className="w-4 h-4" />
    </button>
  </div>
);

export const GroupRemindersDialog = ({ open, onOpenChange, businessId }) => {
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const res = await fetch(`${API}/businesses/${businessId}/group-reminders?date=${date}`, { credentials: "include" });
    if (res.ok) setData(await res.json());
  }, [businessId, date]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const toggle = async (a, sent) => {
    await markSent(a.reservation_id, sent);
    setData((d) => ({ ...d, classes: d.classes.map((c) => ({ ...c, attendees: c.attendees.map((x) => (x.reservation_id === a.reservation_id ? { ...x, reminder_sent: sent } : x)) })) }));
  };
  const all = data?.classes.flatMap((c) => c.attendees) || [];
  const sentCount = all.filter((a) => a.reminder_sent).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl w-[95vw] max-h-[88vh] overflow-y-auto" data-testid="group-reminders-dialog">
        <DialogHeader><DialogTitle>Ders Hatırlatma (WhatsApp)</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <DateInputTR value={date} onChange={(e) => setDate(e.target.value)} className="rounded-xl w-44" data-testid="reminders-date" />
          <Badge className="bg-emerald-100 text-emerald-700" data-testid="reminders-progress">{sentCount}/{all.length} gönderildi</Badge>
          <div className="flex-1" />
          <Button variant="outline" size="sm" className="rounded-xl" disabled={!data}
            onClick={() => { navigator.clipboard.writeText(data.generic_message).catch(() => {}); toast.success("Mesaj kopyalandı — yayın listesine yapıştırabilirsiniz"); }}
            data-testid="reminders-copy-message"><Copy className="w-4 h-4 mr-1" /> Mesajı kopyala</Button>
          <Button variant="outline" size="sm" className="rounded-xl" disabled={!all.length}
            onClick={() => { navigator.clipboard.writeText(all.map((a) => a.phone).filter(Boolean).join("\n")).catch(() => {}); toast.success("Numaralar kopyalandı"); }}
            data-testid="reminders-copy-phones"><Copy className="w-4 h-4 mr-1" /> Numaraları kopyala</Button>
        </div>
        <p className="text-xs text-grey">Her WhatsApp butonu hazır mesajla sohbeti açar; siz "Gönder"e basarsınız. Tıkladığınız kişi ✓ olarak işaretlenir.</p>
        {data?.classes.map((c) => (
          <div key={`${c.start_time}-${c.trainer_name}`} className="rounded-xl border p-3" data-testid={`reminder-class-${c.start_time}`}>
            <p className="font-semibold text-navy text-sm mb-1">{c.start_time}–{c.end_time} · {c.branch || "Grup"} · {c.trainer_name} <span className="text-grey font-normal">({c.attendees.length} kişi)</span></p>
            {c.attendees.map((a) => <AttendeeRow key={a.reservation_id} a={a} onToggle={toggle} />)}
          </div>
        ))}
        {data && !data.classes.length && <p className="text-center text-grey text-sm py-8" data-testid="reminders-empty">Bu gün online ders rezervasyonu yok.</p>}
      </DialogContent>
    </Dialog>
  );
};
